import { io } from "socket.io-client";
import { useEffect, useState } from "react";
import type { GuessResult, RoomView } from "../../shared/game-types.js";
import type { Ack, DevGuessTrace } from "../../shared/protocol.js";
import { createStore, useStore } from "./store.js";
import { hydrateTuning } from "./tuning.js";

export const socket = io({
  autoConnect: true,
  transports: ["websocket", "polling"],
});

export const roomStore = createStore<RoomView | null>(null);
export const connectionStore = createStore<"connecting" | "online" | "offline">(
  "connecting",
);

socket.on("connect", () => connectionStore.set("online"));
socket.on("disconnect", () => connectionStore.set("offline"));
socket.on("room:state", (view: RoomView) => roomStore.set(view));
socket.on("room:closed", () => roomStore.set(null));
// A tuner save from any browser refreshes every other one.
socket.on("tuning:changed", () => void hydrateTuning());

export const useRoom = () => useStore(roomStore);
export const useConnection = () => useStore(connectionStore);

/** Promise wrapper over socket.io acks, with one error path for callers. */
export function request<T>(event: string, payload: unknown = {}): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new Error("The server did not respond")),
      8000,
    );
    socket.emit(event, payload, (r: Ack<T>) => {
      clearTimeout(timeout);
      if (r?.ok) resolve(r.data);
      else reject(new Error(r?.error ?? "Something went wrong"));
    });
  });
}

/**
 * Subscribe to resolution events rather than diffing state, so a reconnect that
 * replays the current state cannot re-fire an animation that already played.
 */
export function useGuessResults(handler: (r: GuessResult) => void): void {
  useEffect(() => {
    socket.on("room:result", handler);
    return () => void socket.off("room:result", handler);
  }, [handler]);
}

export function useDevTraces(limit = 200): DevGuessTrace[] {
  const [traces, setTraces] = useState<DevGuessTrace[]>([]);
  useEffect(() => {
    const onTrace = (t: DevGuessTrace) =>
      setTraces((prev) => [t, ...prev].slice(0, limit));
    socket.on("dev:trace", onTrace);
    return () => void socket.off("dev:trace", onTrace);
  }, [limit]);
  return traces;
}

/** Seconds left on the current clock, ticked locally between server updates. */
export function useCountdown(deadline: number | null): number {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (deadline === null) {
      setRemaining(0);
      return;
    }
    const update = () =>
      setRemaining(Math.max(0, (deadline - Date.now()) / 1000));
    update();
    const id = setInterval(update, 100);
    return () => clearInterval(id);
  }, [deadline]);
  return remaining;
}

const STORAGE_KEY = "factfeud:identity";

export interface Identity {
  code: string;
  playerId: string;
  name: string;
}

/**
 * Same-origin iframes share their parent tab's sessionStorage, so the test
 * harness would have every simulated phone resume the same player. Keying by
 * seat gives each frame its own slot; a real phone has no seat and uses the
 * bare key.
 */
function keyFor(seat: string | null): string {
  return seat ? `${STORAGE_KEY}:${seat}` : STORAGE_KEY;
}

/**
 * Whether a stored identity may be resumed for the room the URL names.
 *
 * The dangerous case is subtle: scan a QR for a new room while holding an
 * identity from an old one and `phone:resume` will *succeed*, because the old
 * room is usually still alive on the server. The player then sits in a game
 * nobody is watching. Only the room code can tell the two apart.
 */
export function isIdentityUsable(
  stored: Identity | null,
  urlRoom: string,
): boolean {
  if (!stored) return false;
  // No room in the URL means the phone was opened directly; keep the identity.
  if (!urlRoom) return true;
  return stored.code === urlRoom.toUpperCase();
}

/**
 * Player identity is ephemeral session state, not tuned data, so sessionStorage
 * is the right home for it — it lets a phone reload mid-match without rejoining.
 */
export function loadIdentity(seat: string | null = null): Identity | null {
  try {
    const raw = sessionStorage.getItem(keyFor(seat));
    return raw ? (JSON.parse(raw) as Identity) : null;
  } catch {
    return null;
  }
}

export function saveIdentity(
  identity: Identity | null,
  seat: string | null = null,
): void {
  try {
    if (identity)
      sessionStorage.setItem(keyFor(seat), JSON.stringify(identity));
    else sessionStorage.removeItem(keyFor(seat));
  } catch {
    // Private browsing with storage disabled: rejoining by hand still works.
  }
}
